import { useEffect, useState } from 'react'
import fetchRequest from '@utils/fetchRequest'

/**
 * 后端返回的合作代理商 logo 单项
 * 对应 poseidon-bc-web 接口 GET /overseas/poseidon/agent/cooperationLogos
 */
interface CooperationAgentLogoDTO {
  logo: string
  agentName?: string
  rank?: number | null
}

/**
 * 前端渲染使用的数据结构
 */
export interface CooperationAgentItem {
  id: number | string
  logo: string
}

/**
 * 合作代理商 logo 列表查询接口（免鉴权）
 * 后端读取海马 key overseas_agnent_logos，按 rank 升序返回
 */
const COOPERATION_LOGOS_API = '/overseas/poseidon/agent/cooperationLogos'

/**
 * 合作代理商 logo 列表 Hook
 *
 * - 挂载时拉取海马配置接口，按 rank 升序返回 logo 列表
 * - **接口返回前 agents 为空数组，页面保持空白**（不使用任何默认填充数据）
 * - 接口返回为空或请求异常时，agents 仍为空数组（模块不展示 logo）
 * - 返回数据归一化为 { id, logo }
 *
 * @returns agents logo 列表；loading 是否仍在首次加载中
 */
const useCooperationAgents = () => {
  // 初始为空数组：接口数据返回前不渲染任何默认 logo，保持页面空白
  const [agents, setAgents] = useState<CooperationAgentItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false
    const fetchLogos = async () => {
      try {
        const res = await fetchRequest.get<{ code: number; data: CooperationAgentLogoDTO[] | null }>(
          COOPERATION_LOGOS_API
        )
        if (cancelled) return
        // 响应结构为 { code, data, msg }，code === 200 表示成功
        if (res && +res.code === 200 && Array.isArray(res.data) && res.data.length > 0) {
          // 归一化为 { id, logo }，id 用 rank（缺失则用下标）保证 React key 稳定
          const list: CooperationAgentItem[] = res.data.map((item, index) => ({
            id: item.rank != null ? item.rank : index,
            logo: item.logo,
          }))
          setAgents(list)
        }
        // 接口返回空 list 或异常时，agents 保持空数组，模块不展示 logo
      } catch (error) {
        // 请求异常时不抛错，agents 保持空数组
        console.error('fetch cooperationLogos failed', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    fetchLogos()
    return () => {
      cancelled = true
    }
  }, [])

  return { agents, loading }
}

export default useCooperationAgents
